import React, { FC, useEffect, useState } from 'react';
import {
  APP_DEFAULT_LANGUAGE_KEY,
  LangSelectProfile,
  LANGUAGE_SELECT,
} from 'config';
import Select, {
  ControlProps,
  DropdownIndicatorProps,
  GetOptionLabel,
  SingleValue,
  StylesConfig,
  OptionProps,
} from 'react-select';
import { useTranslation } from 'react-i18next';
import { localStorageManager } from 'services/localStorage';
import * as S from './LanguageSelector.styles';
import { currentLanguageEntity, findLanguageEntity } from './utils/utils';

type LanguageSelectorProps = {
  customDropDownIndicator?: (
    props: DropdownIndicatorProps<LangSelectProfile, false>,
  ) => JSX.Element;
  labelSelector?: GetOptionLabel<LangSelectProfile>;
  customControl?: (
    props: ControlProps<LangSelectProfile, false>,
  ) => JSX.Element;
  customOption?: (props: OptionProps<LangSelectProfile, false>) => JSX.Element;
  customStyle?: StylesConfig<LangSelectProfile, false>;
  blockWidth?: string;
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
};

export const CustomizedLanguageSelector: FC<LanguageSelectorProps> = ({
  customDropDownIndicator,
  labelSelector,
  customControl,
  customOption,
  customStyle,
  blockWidth,
  onMenuOpen,
  onMenuClose,
}) => {
  const { i18n } = useTranslation();

  const [selectLanguage, setSelectLanguage] = useState<LangSelectProfile>(
    currentLanguageEntity ?? LANGUAGE_SELECT[0],
  );
  const localLanguage =
    localStorageManager.getLanguage() || APP_DEFAULT_LANGUAGE_KEY;

  useEffect(() => {
    i18n.changeLanguage(localLanguage);
    setSelectLanguage(findLanguageEntity(localLanguage) ?? LANGUAGE_SELECT[0]);
  }, []);

  const handleLanguage = (currentLanguage: SingleValue<LangSelectProfile>) => {
    if (currentLanguage) {
      const { keyLanguage } = currentLanguage;
      i18n.changeLanguage(keyLanguage);
      localStorageManager.setLanguage(keyLanguage);
      setSelectLanguage(currentLanguage);
    }
  };

  return (
    <S.LanguageStyled blockWidth={blockWidth}>
      <Select<LangSelectProfile, false>
        data-testid='dropdown-language-selector'
        onChange={handleLanguage}
        options={LANGUAGE_SELECT}
        isSearchable={false}
        styles={customStyle}
        components={{
          DropdownIndicator: customDropDownIndicator,
          IndicatorSeparator: () => null,
          Option: customOption,
          Control: customControl,
        }}
        value={selectLanguage}
        getOptionLabel={labelSelector}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
      />
    </S.LanguageStyled>
  );
};
